import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup } from 'reactstrap';
import { kebabCase } from 'lodash';
import { parentPath } from '@truqc/content-path';
import { convertToAbsolutePath } from '../utils';
import { enabledSelector, labelSelector, newPathSelector, onNullDependentSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedSegmentedButton } from './segmentedButton';
import Label from './label';
import logValueOnChange from '../commonActions/logValueOnChange';

// must be defined outside of the component to avoid re-rendering loops
const defaultOnBlur = () => {};
const defaultOnFocus = () => {};
const SinglePickerButton = _ref => {
  let enabled = _ref.enabled,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? defaultOnBlur : _ref$onBlur,
    onChange = _ref.onChange,
    path = _ref.path,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? defaultOnFocus : _ref$onFocus,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$allowsNull = _ref.allowsNull,
    allowsNull = _ref$allowsNull === void 0 ? true : _ref$allowsNull,
    _ref$onNull = _ref.onNull,
    onNull = _ref$onNull === void 0 ? null : _ref$onNull,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie,
    _ref$onNullHasDepende = _ref.onNullHasDependentValue,
    onNullHasDependentValue = _ref$onNullHasDepende === void 0 ? false : _ref$onNullHasDepende,
    notificationActions = _ref.notificationActions;
  const handleChange = useCallback(async option => {
    const displayValue = option ? option.identifier : null;
    // If a valid option was selected
    if (!value || option.identifier !== value.identifier) {
      return onChange({
        path,
        value: option,
        displayValue
      });
    }
    // If the picker isn't nullable
    if (!allowsNull) {
      return null;
    }
    // If there's no dependent data or an onNull definition
    if (!onNull || !onNullHasDependentValue) {
      return onChange({
        path,
        value: null
      });
    }
    // Otherwise, present the nullWarning modal
    const nullWarning = onNull.nullWarning,
      _onNull$dependentCont = onNull.dependentContentPaths,
      dependentContentPaths = _onNull$dependentCont === void 0 ? [] : _onNull$dependentCont;
    return notificationActions.info({
      message: nullWarning.message,
      headerMessage: nullWarning.title,
      rightButtonLabel: nullWarning.nullTitle,
      rightButtonAction: () => {
        dependentContentPaths.forEach(dependentPath => {
          // Set all the values of dependentContentPaths to null
          const absolutePath = "/".concat(convertToAbsolutePath(dependentPath, parentPath(path)));
          onChange({
            path: absolutePath,
            value: null
          });
        });
        onChange({
          path,
          value: null
        });
      },
      leftButtonLabel: nullWarning.cancelTitle,
      id: 'picker-on-null-notification'
    }, {
      autoClose: false
    });
  }, [allowsNull, notificationActions, onChange, path, onNull, onNullHasDependentValue, value]);
  const activeValue = value && typeof value === 'object' ? value.identifier : value;
  const newClassName = label !== null && label.length ? "t-spb t-spb-".concat(kebabCase(label), " ").concat(className) : className;
  const testID = testingIdentifier || "spb-".concat(kebabCase(label));
  return /*#__PURE__*/React.createElement("div", {
    className: "s-spb ".concat(newClassName, " tqc-control "),
    "data-testid": testID
  }, /*#__PURE__*/React.createElement(Label, {
    value: label
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    style: {
      display: 'flex'
    },
    value: activeValue === 0 ? 0 : activeValue || '',
    onBlur: onBlur,
    onFocus: onFocus
  }, options.map(option => /*#__PURE__*/React.createElement(UncomposedSegmentedButton, {
    enabled: enabled,
    key: "SGBTN_".concat(option.identifier),
    active: activeValue === option.identifier,
    onChange: handleChange,
    option: option,
    label: label
  }))));
};
SinglePickerButton.propTypes = {
  allowsNull: PropTypes.bool,
  className: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onNullHasDependentValue: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired,
  path: PropTypes.string.isRequired,
  onNull: PropTypes.shape({
    dependentContentPaths: PropTypes.arrayOf(PropTypes.string),
    nullWarning: PropTypes.shape({
      title: PropTypes.string,
      message: PropTypes.string,
      cancelTitle: PropTypes.string,
      nullTitle: PropTypes.string
    })
  }),
  testingIdentifier: PropTypes.string,
  value: PropTypes.shape({
    identifier: PropTypes.string
  })
};
export { SinglePickerButton as UncomposedSinglePickerButton };
export default entityComposition(SinglePickerButton, [], {
  enabled: enabledSelector,
  label: labelSelector,
  onNullHasDependentValue: onNullDependentSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
}, ['notification']);